<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWebSiteStore } from '@/stores/website.store'
import { useNotify } from '@/composables/notify'
import { redeemAppSumoCodeApi } from '@/apis/appsumo.api'
import i18n from '@/plugins/i18n'

const websiteStore = useWebSiteStore()
const router = useRouter()
const { notifyError, notifySuccess, notifyWarning } = useNotify()

const email = ref('')
const password = ref('')
const code = ref('')
const isPwd = ref(true)
const loading = ref(false)

// Result state
const done = ref(false)
const createdAccount = ref(false)
const codesCount = ref(0)
const numbers = ref(0)
const maxTierReached = ref(false)
// multi-code paste: per-code outcome
const submitted = ref(0)
const redeemedNow = ref(0)
const failed = ref(0)
const results = ref([])

// Translations are served from the backend (localizations API). Fall back to readable
// English copy so the page is fully usable before those keys exist in the DB.
const FALLBACKS = {
  'appsumo.title': 'Redeem your AppSumo code',
  'appsumo.subtitle': 'Enter the code you received from AppSumo to activate your ZapLater lifetime deal.',
  'appsumo.codeLabel': 'AppSumo Code',
  'appsumo.codeHint': 'ZAP-XXXXX-XXXXX-XXXXX — got several codes? Paste them all, one per line.',
  'appsumo.someCodesFailed': 'Some codes could not be redeemed — see the details below.',
  'appsumo.codesDetail': 'Codes submitted',
  'appsumo.statusRedeemed': 'redeemed',
  'appsumo.statusIdempotent': 'already yours',
  'appsumo.statusError': 'not redeemed',
  'appsumo.codeRedeemed': 'Redeemed',
  'appsumo.codeAlreadyRedeemedByYou': 'Already on your account',
  'error.appsumo.tooManyAttempts': 'Too many wrong codes. Please wait before trying again.',
  'error.appsumo.temporarilyUnavailable': 'Code redemption is temporarily unavailable. Please try again shortly.',
  'appsumo.createAccountHint': 'Enter the email and password of your ZapLater account. New here? These become your login.',
  'appsumo.redeem': 'Redeem code',
  'appsumo.signedOutNote': 'You were signed out so your codes go to the account you enter below.',
  'appsumo.successTitle': 'Code redeemed',
  'appsumo.checkEmail': 'We sent you a confirmation email: confirm your address to activate your login.',
  'appsumo.codesRedeemed': 'AppSumo codes redeemed',
  'appsumo.numbersUnlocked': 'WhatsApp numbers unlocked',
  'appsumo.planName': 'Lifetime Pro Bundle (scheduling + WarmZap)',
  'appsumo.moreCodes': 'Do you have more AppSumo codes? Add them here to move up a tier.',
  'appsumo.addAnother': 'Add another code',
  'appsumo.maxTier': 'You have reached the maximum tier (10 numbers).',
  'appsumo.goToApp': 'Go to ZapLater',
  'appsumo.goToLogin': 'Go to login',
  'appsumo.alreadyAccount': 'Already have a ZapLater account? Enter the same email and password above.',
  'appsumo.signInFirst': 'Sign in first',
  'error.appsumo.invalidCode': 'This code is not valid.',
  'error.appsumo.codeAlreadyUsed': 'This code has already been used.',
  'error.appsumo.maxTierReached': 'You have already reached the maximum tier.',
  'error.duplicatedEmail': 'An account with this email already exists. Sign in and redeem from your account.',
  'error.passwordRules': 'Password too weak: use at least 8 characters, not entirely numeric.',
  'error.invalidEmail': 'Please enter a valid email address.',
  'form.field.required': 'This field is required',
  'form.field.invalidEmail': 'Invalid email address',
}

const tr = (key) => (i18n.global.te(key) ? i18n.global.t(key) : (FALLBACKS[key] || key))

const required = (val) => !!val || tr('form.field.required')
const emailRule = (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || tr('form.field.invalidEmail')

const redeemAsync = async() => {
  try {
    loading.value = true

    // The typed email always decides the account: never the browser session.
    const res = await redeemAppSumoCodeApi(code.value, email.value, password.value)

    const data = res.data || {}
    createdAccount.value = !!data.created_account
    codesCount.value = data.codes_count || 0
    numbers.value = data.numbers || 0
    maxTierReached.value = !!data.max_tier_reached
    submitted.value = data.submitted || 1
    redeemedNow.value = data.redeemed_now || 0
    failed.value = data.failed || 0
    results.value = data.results || []
    done.value = true
    code.value = ''

    // Some codes of the pasted block failed: say so, the rest went through.
    if (failed.value > 0) {
      notifyWarning(tr('appsumo.someCodesFailed'))
    } else {
      notifySuccess(tr('appsumo.successTitle'))
    }
  } catch (error) {
    const data = error.response?.data || {}

    // Rate limited after too many wrong codes: tell the user how long to wait.
    if (error.response?.status === 429) {
      const wait = data.retry_after_seconds || 60
      notifyError(`${tr('error.appsumo.tooManyAttempts')} (${Math.ceil(wait / 60)} min)`)
      return
    }
    if (error.response?.status === 503) {
      notifyError(tr('error.appsumo.temporarilyUnavailable'))
      return
    }

    // Backend returns {code, message} for redeem errors, or DRF field errors for bad input.
    if (data.message) {
      notifyError(tr(data.message))
    } else if (typeof data === 'object') {
      Object.keys(data).forEach((field) => {
        const val = data[field]
        if (Array.isArray(val)) {
          val.forEach((msg) => notifyError(tr(msg)))
        } else if (typeof val === 'string') {
          notifyError(tr(val))
        }
      })
    } else {
      notifyError(tr('error.appsumo.invalidCode'))
    }
  } finally {
    loading.value = false
  }
}

const addAnother = () => {
  done.value = false
}

/**
 * Arriving here always starts from a clean slate: any open session is closed.
 *
 * Why: on 2026-07-30 a buyer opened this page while logged into another account. The form
 * hid the email/password fields and the codes went onto that account — no email ever typed,
 * ten numbers granted to the wrong place. Now the page never trusts the session: it signs the
 * browser out, and the email typed in the form is the single thing that decides the account.
 *
 * logoutAsync() also blacklists the refresh token server-side (good) and redirects to /login,
 * so we come straight back here.
 */
const signedOutOnArrival = ref(false)

onMounted(async() => {
  if (!websiteStore.isLogged()) {
    return
  }
  try {
    await websiteStore.logoutAsync()
  } catch (e) {
    // the store clears the local session even if the API call fails
  } finally {
    signedOutOnArrival.value = true
    router.replace('/appsumo-lifetime-code-redemption')
  }
})
</script>

<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="flex flex-center bg-gradient">
        <div class="column items-center q-gutter-md">
          <!-- ===== FORM ===== -->
          <template v-if="!done">
            <div class="text-h5 text-center">
              {{ tr('appsumo.title') }}
            </div>
            <div class="text-subtitle2 text-center text-white appsumo-subtitle">
              {{ tr('appsumo.subtitle') }}
            </div>

            <q-banner
              v-if="signedOutOnArrival"
              dense
              class="appsumo-signout-note"
            >
              <template #avatar>
                <q-icon
                  name="mdi-information-outline"
                  color="primary"
                />
              </template>
              {{ tr('appsumo.signedOutNote') }}
            </q-banner>

            <q-form
              class="q-mt-md column login-card"
              @submit.prevent="redeemAsync"
            >
              <!-- Email + password are ALWAYS asked, even if a session is open. The typed email
                   is what decides the account: a buyer who arrived with someone else's session
                   open must not silently redeem onto it (real mishap, 2026-07-30). -->
              <div class="text-caption text-white q-mb-sm">
                {{ tr('appsumo.createAccountHint') }}
              </div>

                <q-input
                  v-model="email"
                  outlined
                  type="email"
                  autocapitalize="off"
                  :placeholder="$t('auth.email')"
                  :rules="[required, emailRule]"
                >
                  <template #prepend>
                    <q-icon name="mdi-email" />
                  </template>
                </q-input>

                <q-input
                  v-model="password"
                  outlined
                  class="q-mt-md"
                  :type="isPwd ? 'password' : 'text'"
                  :placeholder="$t('auth.password')"
                  :rules="[required]"
                >
                  <template #prepend>
                    <q-icon name="mdi-lock" />
                  </template>
                  <template #append>
                    <q-icon
                      :name="isPwd ? 'mdi-eye-off' : 'mdi-eye'"
                      class="cursor-pointer"
                      @click="isPwd = !isPwd"
                    />
                  </template>
                </q-input>

              <!-- AppSumo Code (label required by AppSumo review).
                   Textarea: a top-tier buyer gets 5 codes and pastes them in one go. -->
              <q-input
                v-model="code"
                outlined
                autogrow
                type="textarea"
                class="q-mt-md"
                autocapitalize="characters"
                :label="tr('appsumo.codeLabel')"
                :hint="tr('appsumo.codeHint')"
                :rules="[required]"
              >
                <template #prepend>
                  <q-icon name="mdi-ticket-confirmation" />
                </template>
              </q-input>

              <div class="q-mt-lg">
                <q-btn
                  color="primary"
                  class="full-width q-pa-sm"
                  :label="tr('appsumo.redeem')"
                  :loading="loading"
                  type="submit"
                />
              </div>

              <div class="login-actions column items-center q-mt-md">
                <div class="no-account row items-center">
                  <span class="text-white">{{ tr('appsumo.alreadyAccount') }}</span>
                  <router-link
                    to="/login"
                    class="signup-link q-ml-sm"
                  >
                    {{ tr('appsumo.signInFirst') }}
                  </router-link>
                </div>
              </div>
            </q-form>
          </template>

          <!-- ===== SUCCESS ===== -->
          <template v-else>
            <q-icon
              name="mdi-check-circle-outline"
              color="positive"
              size="64px"
            />
            <div class="text-h5 text-center">
              {{ tr('appsumo.successTitle') }}
            </div>

            <q-card
              flat
              bordered
              class="appsumo-result q-pa-md"
            >
              <div class="text-subtitle1 text-weight-medium q-mb-sm">
                {{ tr('appsumo.planName') }}
              </div>

              <div class="row items-center justify-between q-py-xs">
                <span>{{ tr('appsumo.codesRedeemed') }}</span>
                <q-badge
                  color="primary"
                  :label="codesCount"
                />
              </div>
              <div class="row items-center justify-between q-py-xs">
                <span>{{ tr('appsumo.numbersUnlocked') }}</span>
                <q-badge
                  color="positive"
                  :label="numbers"
                />
              </div>

              <!-- per-code outcome, shown only when more than one code was pasted -->
              <template v-if="submitted > 1">
                <q-separator class="q-my-md" />
                <div class="text-caption text-weight-medium q-mb-xs">
                  {{ tr('appsumo.codesDetail') }}: {{ submitted }}
                </div>
                <div
                  v-for="(r, i) in results"
                  :key="i"
                  class="row items-center justify-between text-caption q-py-xs"
                >
                  <span class="appsumo-code-mono">{{ r.code }}</span>
                  <q-badge
                    :color="r.status === 'error' ? 'negative' : (r.status === 'idempotent' ? 'grey-6' : 'positive')"
                    :label="r.status === 'error' ? tr(r.message) : tr('appsumo.status' + (r.status === 'idempotent' ? 'Idempotent' : 'Redeemed'))"
                  />
                </div>
              </template>

              <q-separator class="q-my-md" />

              <div
                v-if="createdAccount"
                class="text-caption q-mb-sm"
              >
                <q-icon
                  name="mdi-email-fast-outline"
                  class="q-mr-xs"
                />{{ tr('appsumo.checkEmail') }}
              </div>

              <div
                v-if="maxTierReached"
                class="text-caption text-positive"
              >
                {{ tr('appsumo.maxTier') }}
              </div>
              <div
                v-else
                class="text-caption"
              >
                {{ tr('appsumo.moreCodes') }}
              </div>
            </q-card>

            <!-- Primary action: unmissable. It used to be a flat button that read as plain
                 text, so the one step people actually had to take went unnoticed. -->
            <q-btn
              unelevated
              size="lg"
              color="primary"
              class="appsumo-cta q-mt-md"
              :label="tr('appsumo.goToLogin')"
              icon-right="mdi-login"
              @click="router.push('/login')"
            />

            <q-btn
              v-if="!maxTierReached"
              flat
              no-caps
              color="white"
              class="q-mt-xs"
              :label="tr('appsumo.addAnother')"
              @click="addAnother"
            />
          </template>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/auth-forms.scss';

.appsumo-subtitle {
  max-width: 420px;
  opacity: 0.85;
}

.appsumo-result {
  min-width: 320px;
  max-width: 420px;
  // The auth layout paints text white on the gradient; inside the white card that would be
  // invisible, so pin readable colors explicitly.
  background: #ffffff;
  color: #1d1d1d;

  :deep(.text-caption) {
    color: #5b5b5b;
  }

  :deep(.text-caption.text-positive) {
    color: var(--q-positive);
  }
}

.appsumo-code-mono {
  font-family: monospace;
  letter-spacing: 0.02em;
}

// Primary call to action after a successful redeem: it must read as "do this now".
// Gentle pulse, disabled for users who asked for reduced motion.
.appsumo-cta {
  min-width: 260px;
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  animation: appsumo-cta-pulse 2.4s ease-in-out infinite;

  &:hover {
    animation-play-state: paused;
    transform: translateY(-1px);
  }
}

@keyframes appsumo-cta-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.035); }
}

@media (prefers-reduced-motion: reduce) {
  .appsumo-cta {
    animation: none;
  }
}

// "You are redeeming onto THIS account": white card on the green layout, so it cannot be missed.
.appsumo-signout-note {
  background: #ffffff;
  color: #1d1d1d;
  border-radius: 6px;
  max-width: 420px;
}

.appsumo-account-banner {
  background: #ffffff;
  color: #1d1d1d;
  border-radius: 6px;

  .appsumo-account-email {
    font-family: monospace;
    font-size: 1.02em;
    font-weight: 600;
    word-break: break-all;
  }
}
</style>
